/**
 * src/lib/api.js — the single place the frontend talks to the API.
 *
 * Everything goes through `request`, which centralises three things that are
 * easy to get subtly wrong if each component does its own fetch:
 *
 *  - **Credentials.** The session is an httpOnly cookie, so every call needs
 *    `credentials: 'include'`. A call that forgets it silently behaves as
 *    logged out.
 *  - **Errors.** The API returns `{ error: "..." }` with a real status code.
 *    `request` turns that into a thrown ApiError carrying the status, so
 *    components can `catch` and show the server's own message rather than a
 *    generic one.
 *  - **401 handling.** An expired session must land the user on /login rather
 *    than in a half-rendered page full of failed requests.
 */

/** Base URL for the API. Same-origin in dev (Vite proxies /api). */
const BASE = import.meta.env.VITE_API_BASE ?? '';

/**
 * An error carrying the HTTP status the API returned.
 */
export class ApiError extends Error {
  /**
   * @param {string} message Server-supplied message where available.
   * @param {number} status HTTP status code.
   */
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Set by App.jsx so a 401 anywhere can bounce the user to /login. */
let onUnauthorised = () => {};

/**
 * Registers the callback invoked when any request comes back 401.
 *
 * @param {() => void} handler
 * @returns {void}
 */
export function setUnauthorisedHandler(handler) {
  onUnauthorised = handler;
}

/**
 * Performs an API request.
 *
 * @param {string} path Path beginning with /api.
 * @param {{ method?: string, body?: unknown, signal?: AbortSignal }} [opts]
 * @returns {Promise<any>} Parsed JSON body.
 * @throws {ApiError} For any non-2xx response.
 */
export async function request(path, opts = {}) {
  const { method = 'GET', body, signal } = opts;

  const init = {
    method,
    // The session cookie is httpOnly and cross-origin in production.
    credentials: 'include',
    headers: {},
    signal,
  };
  if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const res = await fetch(`${BASE}${path}`, init);

  if (res.status === 401) {
    onUnauthorised();
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }

  // 204 and empty bodies are valid; don't try to parse them.
  const text = await res.text();

  // Never let a non-JSON body become a SyntaxError. The API answers JSON for
  // every status, but the things in front of it do not: a Cloudflare error
  // page, a 502 from a cold start, a captcha interstitial. Throwing a parse
  // error there would hide the actual status from the user entirely.
  let payload = {};
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { error: text.slice(0, 200) };
    }
  }

  if (!res.ok) {
    throw new ApiError(payload.error ?? `Request failed (${res.status})`, res.status);
  }
  return payload;
}

/** Shorthand helpers, so components read as `api.get('/api/clients')`. */
export const api = {
  /** @param {string} path @returns {Promise<any>} */
  get: (path) => request(path),
  /** @param {string} path @param {unknown} body @returns {Promise<any>} */
  post: (path, body) => request(path, { method: 'POST', body }),
  /** @param {string} path @param {unknown} body @returns {Promise<any>} */
  patch: (path, body) => request(path, { method: 'PATCH', body }),
  /** @param {string} path @returns {Promise<any>} */
  del: (path) => request(path, { method: 'DELETE' }),
};

/**
 * Uploads a file: presign, PUT, confirm.
 *
 * The three-step handshake is hidden here so no component has to know about
 * it. See api/src/routes/uploads.js for why it is three steps.
 *
 * @param {File} file The file chosen in a file input.
 * @param {{ assetType: string, projectId?: string, appointmentId?: string }} opts
 * @returns {Promise<{ r2Key: string, publicUrl: string, mediaId: string }>}
 * @throws {ApiError|Error} If any step fails.
 */
export async function uploadFile(file, opts) {
  const presign = await api.post('/api/uploads/presign', {
    fileName: file.name,
    contentType: file.type,
    ...opts,
  });

  const put = await fetch(presign.uploadUrl, {
    method: presign.method ?? 'PUT',
    // The dev fallback route is on this API and needs the session cookie; a
    // real presigned R2 URL ignores it.
    credentials: presign.presigned ? 'omit' : 'include',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);

  await api.post('/api/uploads/confirm', { r2Key: presign.r2Key });
  return { r2Key: presign.r2Key, publicUrl: presign.publicUrl, mediaId: presign.mediaId };
}
